/**
 * Telefone com seletor de DDI e máscara brasileira.
 *
 * Emite sempre no formato armazenado — `+` seguido só de dígitos (E.164). A
 * máscara existe na tela e morre nela. O backend normaliza de novo, por garantia:
 * a apresentação nunca é fonte da verdade.
 */
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { ChevronDown } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { Label } from '@/components/ui/label';
import {
  apenasDigitos,
  detectarPais,
  digitosLocais,
  mascaraBR,
  PAIS_PADRAO,
  PAISES,
  type Pais,
} from '@/lib/telefone';
import { cn } from '@/lib/utils';

export function CampoTelefone({
  rotulo,
  valor,
  onChange,
  id,
}: {
  rotulo: string;
  /** E.164: `+5551995453808`. Vazio quando não há número. */
  valor: string;
  onChange: (e164: string) => void;
  id?: string;
}) {
  const gerado = useId();
  const campoId = id ?? gerado;
  const campo = useRef<HTMLInputElement>(null);

  const paisInicial = valor ? detectarPais(valor) : PAIS_PADRAO;
  const digitosIniciais = valor ? digitosLocais(valor, paisInicial) : '';

  const [pais, setPais] = useState<Pais>(paisInicial);
  const [digitos, setDigitos] = useState(digitosIniciais);

  // Ressincroniza quando o valor externo muda (ex.: abrir a edição de outro usuário).
  useEffect(() => {
    const p = valor ? detectarPais(valor) : PAIS_PADRAO;
    setPais(p);
    setDigitos(valor ? digitosLocais(valor, p) : '');
  }, [valor]);

  const exibicao = pais.codigo === 'BR' ? mascaraBR(digitos) : digitos;

  function escolherPais(p: Pais) {
    setPais(p);
    onChange(digitos ? `${p.ddi}${digitos}` : '');
    setTimeout(() => campo.current?.focus(), 0);
  }

  function digitar(bruto: string) {
    // Fora do Brasil não limitamos o comprimento: não sabemos o formato local, e
    // truncar produziria número errado com aparência de certo.
    const d = pais.codigo === 'BR' ? apenasDigitos(bruto).slice(0, 11) : apenasDigitos(bruto);
    setDigitos(d);
    onChange(d ? `${pais.ddi}${d}` : '');
  }

  return (
    <div className="flex flex-col gap-2">
      <Label htmlFor={campoId}>{rotulo}</Label>
      <div
        className={cn(
          'flex h-10 overflow-hidden rounded-md border border-border bg-input',
          'transition-colors duration-[var(--dur-fast)]',
          'focus-within:border-border-strong focus-within:shadow-[var(--focus-ring)]',
        )}
      >
        <DropdownMenu.Root>
          <DropdownMenu.Trigger
            aria-label={`País: ${pais.nome} (${pais.ddi})`}
            className="flex shrink-0 items-center gap-1.5 border-r border-border px-3 outline-none transition-colors duration-[var(--dur-fast)] hover:bg-surface-hover"
          >
            {/* Só a bandeira aqui; o número do DDI (+55) aparece no dropdown de seleção. */}
            <span className="text-base leading-none">{pais.bandeira}</span>
            <ChevronDown className="size-3 text-text-tertiary" />
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="start"
              sideOffset={6}
              // Acima da modal: quando o campo vive dentro de um Dialog (z-modal), o menu de
              // países precisa de z maior, senão abre atrás. `z-tooltip` fica acima de tudo.
              className="z-[var(--z-tooltip)] max-h-72 min-w-64 overflow-y-auto rounded-lg border border-border bg-surface p-1 shadow-[var(--shadow-md)]"
            >
              {PAISES.map((p) => (
                <DropdownMenu.Item
                  key={p.codigo}
                  onSelect={() => escolherPais(p)}
                  className={cn(
                    'flex cursor-pointer items-center gap-3 rounded-sm px-3 py-2 text-[15px] outline-none',
                    'data-[highlighted]:bg-surface-hover',
                    pais.codigo === p.codigo && 'text-accent',
                  )}
                >
                  <span className="text-base">{p.bandeira}</span>
                  <span className="w-12 shrink-0 font-mono text-xs text-text-tertiary">{p.ddi}</span>
                  <span>{p.nome}</span>
                </DropdownMenu.Item>
              ))}
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>

        <input
          id={campoId}
          ref={campo}
          type="tel"
          inputMode="numeric"
          required
          placeholder={pais.codigo === 'BR' ? '(51) 99999-8888' : 'somente números'}
          value={exibicao}
          onChange={(e) => digitar(e.target.value)}
          className="flex-1 bg-transparent px-3 font-mono text-[15px] text-text outline-none placeholder:font-sans placeholder:text-text-tertiary"
        />
      </div>
    </div>
  );
}
